import React from 'react';
import styled from 'styled-components';
import { Text, Divider } from 'components';
import {
  SecondaryText,
  SecondaryDescriptionText,
} from '@brokerbay/design-components';
import { UserOutlined } from '@ant-design/icons';

export enum VerificationStatus {
  unsent = 'unsent',
  pending = 'pending',
  success = 'success',
}

type VerificationStatusStyle = {
  text: string;
  color: string;
};

const VerificationStatusMap: Record<
  VerificationStatus,
  VerificationStatusStyle
> = {
  unsent: {
    text: 'Verification unsent',
    color: '#D9D9D9',
  },
  pending: {
    text: 'Verification pending',
    color: '#FAAD14',
  },
  success: {
    text: 'Verification successful',
    color: '#52C41A',
  },
};

type VerificationCardProps = {
  approverTerm: string;
  brokerage: string;
  name: string;
  email: string;
  status: VerificationStatus;
  lastSentDate?: Date;
};

const dateOptions = {
  month: 'short' as 'short' | 'numeric' | '2-digit' | 'long' | 'narrow',
  day: 'numeric' as 'numeric' | '2-digit',
  year: 'numeric' as 'numeric' | '2-digit',
  hour: 'numeric' as 'numeric' | '2-digit',
  minute: 'numeric' as 'numeric' | '2-digit',
};

const VerificationCard = ({
  approverTerm,
  brokerage,
  name,
  email,
  status,
  lastSentDate,
}: VerificationCardProps) => {
  const { color, text } = VerificationStatusMap[status];

  return (
    <StyledApprover>
      <ColumnCentered>
        <ApproverTerm>{approverTerm}</ApproverTerm>
        <ApproverBrokerage>{brokerage}</ApproverBrokerage>
      </ColumnCentered>
      <ApproverDetails>
        <StyledIcon>
          <StyledUserOutlined />
        </StyledIcon>
        <Text>{name}</Text>
        <StyledSecondaryText textAlign="center">{email}</StyledSecondaryText>
      </ApproverDetails>
      <StyledDivider />
      <ColumnCentered>
        <RowCentered>
          <VerificationStatusDot
            style={{
              backgroundColor: color,
            }}
          />
          <VerificationStatusText>{text}</VerificationStatusText>
        </RowCentered>
        {lastSentDate && (
          <SecondaryDescriptionText>
            Last sent:{' '}
            {new Date(lastSentDate).toLocaleDateString(undefined, dateOptions)}
          </SecondaryDescriptionText>
        )}
      </ColumnCentered>
    </StyledApprover>
  );
};

const StyledApprover = styled.div`
  border: 1px solid #f0f0f0;
  padding: 16px;
`;

const ColumnCentered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ApproverTerm = styled(Text)`
  font-weight: 600;
`;

const ApproverBrokerage = styled(Text)`
  margin-top: 1.5rem;
  text-align: center !important;
`;

const ApproverDetails = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  margin: 1rem;
`;

const StyledIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  background-color: #bfbfbf;
  margin-bottom: 4px;
`;

const StyledUserOutlined = styled(UserOutlined)`
  color: #ffffff;
`;

const StyledDivider = styled(Divider)`
  margin: 16px 0;
`;

const RowCentered = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const VerificationStatusDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-right: 1rem;
`;

const VerificationStatusText = styled(Text)`
  color: rgba(0, 0, 0, 0.65);
`;

const StyledSecondaryText = styled(SecondaryText)`
  width: 100% !important;
`;

export default VerificationCard;
